import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, initials, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRoles, labelForRole } from "../lib/roles";
import { toast } from "sonner";
import {
  Loader2, Save, Upload, Trash2, Pencil, X, Plus, Phone, MapPin,
  Linkedin, Mail, GraduationCap, Award, Languages, Sparkles, Calendar as CalIcon, Briefcase,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const roles = useRoles();

  const isSelf = !id || id === me?.id;
  const userId = isSelf ? me?.id : id;
  const canEdit = isSelf || me?.role === "super_admin";

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const url = isSelf ? "/users/me/profile" : `/users/${userId}/profile`;
    const r = await api.get(url);
    setProfile(r.data);
  };

  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  const avatarUrl = profile?.has_avatar
    ? `${BACKEND}/api/users/${userId}/avatar?v=${profile?.avatar_version || 1}`
    : null;

  const onAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop volumineuse (max 2 Mo)"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${userId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Avatar mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'upload");
    } finally { setUploadingAvatar(false); }
  };

  const onAvatarDelete = async () => {
    if (!window.confirm("Supprimer l'avatar ?")) return;
    try {
      await api.delete(`/users/${userId}/avatar`);
      await load();
      toast.success("Avatar supprimé");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  if (!profile) return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-md p-6 flex flex-col sm:flex-row items-start gap-6">
        <div className="relative shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt={profile.full_name} className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-md" data-testid="profile-avatar" />
          ) : (
            <div className="w-28 h-28 rounded-full bg-btp-navy text-white flex items-center justify-center text-3xl font-heading font-bold shadow-md" data-testid="profile-avatar-fallback">
              {initials(profile.full_name)}
            </div>
          )}
          {canEdit && (
            <div className="absolute -bottom-1 -right-1 flex gap-1">
              <button onClick={() => fileRef.current?.click()} disabled={uploadingAvatar}
                className="w-8 h-8 rounded-full bg-btp-terra hover:bg-btp-terra-hover text-white flex items-center justify-center shadow-md disabled:opacity-60"
                title="Changer l'avatar" data-testid="avatar-upload-btn">
                {uploadingAvatar ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              </button>
              {avatarUrl && (
                <button onClick={onAvatarDelete} className="w-8 h-8 rounded-full bg-white border border-slate-200 hover:bg-red-50 text-red-600 flex items-center justify-center shadow-md" title="Supprimer l'avatar" data-testid="avatar-delete-btn">
                  <Trash2 size={14} />
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onAvatarUpload} className="hidden" data-testid="avatar-file-input" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-1">Profil</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="profile-full-name">{profile.full_name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            <span className="text-xs px-2 py-0.5 bg-btp-navy/10 text-btp-navy rounded uppercase tracking-wider font-semibold" data-testid="profile-role-badge">
              {labelForRole(profile.role, roles)}
            </span>
            {profile.expertise && <span className="text-slate-700">· {profile.expertise}</span>}
            {profile.years_of_experience != null && (
              <span className="inline-flex items-center gap-1 text-btp-terra font-semibold" data-testid="profile-years-exp">
                <Briefcase size={13} /> {profile.years_of_experience} an{profile.years_of_experience > 1 ? "s" : ""} d'expérience
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
            {profile.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> <a href={`mailto:${profile.email}`} className="font-mono hover:text-btp-navy">{profile.email}</a></span>}
            {profile.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {profile.phone}</span>}
            {profile.address && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {profile.address}</span>}
            {profile.linkedin_url && <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-btp-navy hover:underline"><Linkedin size={12} /> LinkedIn</a>}
            <span className="inline-flex items-center gap-1 text-slate-400"><CalIcon size={12} /> inscrit le {fmtDate(profile.created_at)}</span>
          </div>
        </div>

        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="h-10 px-4 bg-btp-navy hover:bg-btp-navy/90 text-white rounded-md text-sm font-semibold flex items-center gap-2 shrink-0" data-testid="profile-edit-btn">
            <Pencil size={14} /> Modifier
          </button>
        )}
      </div>

      {editing ? (
        <ProfileEditor profile={profile} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); }} userId={userId} isSelf={isSelf} saving={saving} setSaving={setSaving} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Présentation */}
          <Card title="Présentation" icon={Sparkles} testid="card-bio">
            {profile.bio ? <p className="text-sm text-slate-700 whitespace-pre-line">{profile.bio}</p> :
              <Empty>Aucune présentation renseignée.</Empty>}
          </Card>

          {/* Compétences + Langues */}
          <Card title="Compétences" icon={Award} testid="card-skills">
            {profile.skills?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {profile.skills.map((s, i) => <span key={i} className="text-xs px-2 py-0.5 bg-btp-navy/10 text-btp-navy rounded">{s}</span>)}
              </div>
            ) : <Empty>Aucune compétence renseignée.</Empty>}
            {profile.languages?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1"><Languages size={12} /> Langues</div>
                <div className="flex flex-wrap gap-1.5">
                  {profile.languages.map((l, i) => <span key={i} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded">{l}</span>)}
                </div>
              </div>
            )}
          </Card>

          {/* Diplômes + Certifications */}
          <Card title="Diplômes & certifications" icon={GraduationCap} testid="card-diplomas">
            {profile.diplomas?.length ? (
              <ul className="space-y-3 text-sm">
                {profile.diplomas.map((d, i) => (
                  <li key={i} className="border-l-2 border-btp-terra pl-3">
                    <div className="font-semibold text-slate-800">{d.title}</div>
                    <div className="text-xs text-slate-500">{d.institution}{d.year ? ` · ${d.year}` : ""}{d.field ? ` · ${d.field}` : ""}</div>
                  </li>
                ))}
              </ul>
            ) : <Empty>Aucun diplôme renseigné.</Empty>}
            {profile.certifications?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Certifications</div>
                <ul className="space-y-2 text-sm">
                  {profile.certifications.map((c, i) => (
                    <li key={i}>
                      <span className="font-semibold">{c.name}</span>
                      <span className="text-xs text-slate-500">{c.issuer ? ` — ${c.issuer}` : ""}{c.year ? ` (${c.year})` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
        {Icon && <Icon size={15} className="text-btp-navy" />}
        <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-slate-700">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-xs text-slate-400 italic">{children}</div>;
}

function ProfileEditor({ profile, onCancel, onSaved, userId, isSelf, saving, setSaving }) {
  const [f, setF] = useState({
    full_name: profile.full_name || "",
    expertise: profile.expertise || "",
    phone: profile.phone || "",
    address: profile.address || "",
    bio: profile.bio || "",
    career_start_year: profile.career_start_year || "",
    linkedin_url: profile.linkedin_url || "",
    languages: profile.languages || [],
    skills: profile.skills || [],
    diplomas: profile.diplomas || [],
    certifications: profile.certifications || [],
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        career_start_year: f.career_start_year ? parseInt(f.career_start_year, 10) : null,
      };
      const url = isSelf ? "/users/me/profile" : `/users/${userId}/profile`;
      await api.put(url, payload);
      toast.success("Profil enregistré");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6" data-testid="profile-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nom complet"><input className="input" value={f.full_name} onChange={set("full_name")} data-testid="edit-full-name" /></Field>
        <Field label="Expertise"><input className="input" value={f.expertise} onChange={set("expertise")} placeholder="Géotechnicien…" data-testid="edit-expertise" /></Field>
        <Field label="Téléphone"><input className="input" value={f.phone} onChange={set("phone")} placeholder="+221 77 …" data-testid="edit-phone" /></Field>
        <Field label="Adresse"><input className="input" value={f.address} onChange={set("address")} data-testid="edit-address" /></Field>
        <Field label="LinkedIn"><input className="input" value={f.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" data-testid="edit-linkedin" /></Field>
        <Field label="Année de début d'exercice">
          <input className="input" type="number" min="1950" max={new Date().getFullYear()} value={f.career_start_year} onChange={set("career_start_year")} placeholder="2010" data-testid="edit-career-year" />
          {f.career_start_year && Number(f.career_start_year) >= 1950 && Number(f.career_start_year) <= new Date().getFullYear() && (
            <div className="text-xs text-btp-terra font-semibold mt-1">
              → {new Date().getFullYear() - Number(f.career_start_year)} an(s) d'expérience auto-calculé
            </div>
          )}
        </Field>
      </div>

      <Field label="Présentation / bio">
        <textarea rows={4} className="input" style={{ height: "auto", padding: "0.75rem" }} value={f.bio} onChange={set("bio")} placeholder="Présentez votre parcours, vos spécialités, vos projets phares…" data-testid="edit-bio" />
      </Field>

      <TagsEditor label="Compétences" tags={f.skills} onChange={(v) => setF({ ...f, skills: v })} placeholder="Ajouter une compétence…" testid="edit-skills" />
      <TagsEditor label="Langues" tags={f.languages} onChange={(v) => setF({ ...f, languages: v })} placeholder="Français, Anglais…" testid="edit-languages" />

      <ListEditor label="Diplômes" testid="edit-diplomas"
        items={f.diplomas} onChange={(v) => setF({ ...f, diplomas: v })}
        fields={[
          { key: "title", label: "Intitulé", placeholder: "Ingénieur Génie Civil" },
          { key: "institution", label: "Établissement", placeholder: "ESP Dakar" },
          { key: "year", label: "Année", placeholder: "2009", type: "number" },
          { key: "field", label: "Spécialité", placeholder: "Routes & ouvrages" },
        ]} />

      <ListEditor label="Certifications" testid="edit-certifications"
        items={f.certifications} onChange={(v) => setF({ ...f, certifications: v })}
        fields={[
          { key: "name", label: "Nom", placeholder: "PMP" },
          { key: "issuer", label: "Émetteur", placeholder: "PMI" },
          { key: "year", label: "Année", placeholder: "2022", type: "number" },
        ]} />

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <button onClick={onCancel} className="h-10 px-4 border border-slate-200 hover:bg-slate-50 rounded-md text-sm" data-testid="profile-cancel">Annuler</button>
        <button onClick={save} disabled={saving} className="h-10 px-4 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-60 text-white rounded-md text-sm font-semibold flex items-center gap-2" data-testid="profile-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>

      <style>{`.input{ margin-top:0.375rem; width:100%; height:2.5rem; padding:0 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; } .input:focus{ outline:none; border-color:#1e3a5f; box-shadow:0 0 0 1px #1e3a5f; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function TagsEditor({ label, tags, onChange, placeholder, testid }) {
  const [v, setV] = useState("");
  const add = () => { const x = v.trim(); if (!x) return; if (!tags.includes(x)) onChange([...tags, x]); setV(""); };
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
      <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
        {tags.map((t, i) => (
          <span key={i} className="text-xs pl-2 pr-1 py-0.5 bg-btp-navy/10 text-btp-navy rounded flex items-center gap-1">
            {t}
            <button type="button" onClick={() => onChange(tags.filter((_, j) => j !== i))} className="hover:text-red-600"><X size={10} /></button>
          </span>
        ))}
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="h-8 px-2 border border-slate-300 rounded text-xs flex-1 min-w-[160px]" placeholder={placeholder} data-testid={testid} />
        <button type="button" onClick={add} className="h-8 px-2 border border-slate-300 rounded text-xs hover:bg-slate-50"><Plus size={12} /></button>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, fields, testid }) {
  const empty = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const add = () => onChange([...items, { ...empty }]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const update = (i, k, v) => onChange(items.map((it, j) => j === i ? { ...it, [k]: v } : it));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
        <button type="button" onClick={add} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1"><Plus size={12} /> Ajouter</button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-xs text-slate-400 italic">Aucun élément.</div>}
        {items.map((it, i) => (
          <div key={i} className="p-3 border border-slate-200 rounded bg-slate-50 grid grid-cols-1 sm:grid-cols-4 gap-2 items-start relative">
            {fields.map((f) => (
              <div key={f.key} className={`${fields.length === 4 ? "" : "sm:col-span-1"}`}>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{f.label}</label>
                <input type={f.type || "text"} value={it[f.key] ?? ""} onChange={(e) => update(i, f.key, f.type === "number" ? (e.target.value ? parseInt(e.target.value, 10) : "") : e.target.value)}
                  placeholder={f.placeholder} className="mt-1 w-full h-8 px-2 border border-slate-300 rounded text-xs" />
              </div>
            ))}
            <button type="button" onClick={() => remove(i)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"><X size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
